import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import {
    handleGetRoleData, handleGetPeopleCountInRole, handleGetGroupsCountInRole, setExceptionHandler, addDetailsBreadcrumb, getModalSwitch,
    removeDetailsBreadcrumb, handleAddGroupsToRole
} from '@app/store/actions';
import { IRole, IFabMenu } from '@app/models';
import { hasOrgPermission, usersCount, groupsCount } from '@app/helpers';
import { ManageSystem } from '@app/store/constants';
import CardCountEdit from './components/cardCountEdit.component';
import { FabMenu, IModalData, ModalComponent } from '@app/components';
import { AddUsersToRoleDialog } from '@app/pages/directoryManager/dialogs/addUsersToRole.dialog';
import { RemoveGroupsFromRoleDialog } from '@app/pages/directoryManager/dialogs/removeGroupsFromRole.dialog';
import { RemoveUsersFromRoleDialog } from '@app/pages/directoryManager/dialogs/removeUsersFromRole.dialog';
import { useParams } from 'react-router';
import { AddGroups } from '../dialogs';
import { rolesService } from '@app/services';

const RoleDetailsPage = () => {
    const thisRole: IRole = useSelector((state: any) => state.roles.role);
    const peopleInRoleCount: number = useSelector((state: any) => state.roles.peopleInRoleCount);
    const groupsInRoleCount: number = useSelector((state: any) => state.roles.groupsInRoleCount);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [isUserRole, setIsUserRole] = useState<boolean>(true);

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const { id: roleId } = useParams<{id: string}>();

    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    useEffect(() => {
        dispatch(handleGetRoleData(roleId));
        dispatch(handleGetPeopleCountInRole(roleId));
        dispatch(handleGetGroupsCountInRole(roleId));
        dispatch(setExceptionHandler(true));

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        dispatch(addDetailsBreadcrumb(thisRole.Name));

        if (thisRole && thisRole !== undefined && thisRole.Name !== undefined && thisRole.Name.toLocaleLowerCase() !== 'user') {
            setIsUserRole(false);
        }
    }, [thisRole]);

    const detectGroupListAction: Function = () => {
        if (hasOrgPermission([ManageSystem])) {
            return groupsEditableList();
        }
        else {
            return groupsReadonlyList();
        }
    };

    const detectUserListAction: Function = () => {
        if (hasOrgPermission([ManageSystem]) && thisRole.Name.toLocaleLowerCase() !== 'user') {
            return usersEditableList();
        }
        else {
            return usersReadonlyList();
        }
    };

    const usersEditableList = () => {
        removeUsersFromRole();
    };

    const usersReadonlyList = () => {
        readonlyUsersFromRole();
    };

    const groupsEditableList = () => {
        removeGroupsFromRole();
    };

    const groupsReadonlyList = () => {
        readonlyGroupsFromRole();
    };

    const menu: IFabMenu = {
        name: 'ADD_PERMISSIONS',
        actions: [
            {
                name: 'ADD_GROUPS_TO_ROLE',
                method: () => addGroupsToRole(),
                iconElement: <i className='icon icon-user-roles' />
            },
            {
                name: 'ADD_USERS_TO_ROLE',
                method: () => addUsersToRole(),
                iconElement: <i className='icon icon-users' />
            }
        ]
    };

    const removeGroupsFromRole = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('REMOVE_GROUPS_FROM_ROLE'),
            modalSwitcher: modalSwitchState,
            form: <RemoveGroupsFromRoleDialog disableEdit={false} />
        });
    };

    const removeUsersFromRole = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('REMOVE_USERS_FROM_ROLE'),
            modalSwitcher: modalSwitchState,
            form: <RemoveUsersFromRoleDialog disableEdit={false}  />
        });
    };

    const readonlyGroupsFromRole = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('REMOVE_GROUPS_FROM_ROLE'),
            modalSwitcher: modalSwitchState,
            form: <RemoveGroupsFromRoleDialog disableEdit={true} />
        });
    };

    const readonlyUsersFromRole = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('REMOVE_USERS_FROM_ROLE'),
            modalSwitcher: modalSwitchState,
            form: <RemoveUsersFromRoleDialog disableEdit={true} />
        });
    };

    const addUsersToRole = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_USERS_TO_ROLE'),
            modalSwitcher: modalSwitchState,
            form: <AddUsersToRoleDialog />
        });
    };

    const addGroupsToRole = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('ADD_GROUPS_TO_ROLE'),
            modalSwitcher: modalSwitchState,
            form: <AddGroups
                dataFunction={rolesService.getGroupsNotInRole}
                requestFunction={handleAddGroupsToRole}
                noDataLabel={'NO_GROUPS_TO_ADD_TO_ROLE'}
            />
        });
    };

    return (
        <>
            <ModalComponent {...modalData} />
            <div className='content--narrow detailPage'>
                <div className='detailPage__section'>
                    <div className='detailPage__section-head'>
                        {translation('ROLES_DETAILS')}
                    </div>
                    <div className='detailPage__section-content'>
                        <div className='rowcontainer'>
                            <ul className='detailPage__list'>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('ROLES_NAME')}</span>
                                    <span className='detailPage__list--secondary-label'>{thisRole.Name}</span>
                                </li>
                            </ul>
                            {peopleInRoleCount > 0 &&
                                <CardCountEdit
                                    icon='icon icon-users'
                                    bottomLabel={translation(usersCount(peopleInRoleCount))}
                                    count={peopleInRoleCount}
                                    action={() => detectUserListAction()}
                                />
                            }
                            {groupsInRoleCount > 0 &&
                                <CardCountEdit
                                    icon='icon icon-user-groups'
                                    bottomLabel={translation(groupsCount(groupsInRoleCount))}
                                    count={groupsInRoleCount}
                                    action={() => detectGroupListAction()}
                                />
                            }
                        </div>
                     </div>
                </div>
            </div>

            {hasOrgPermission([ManageSystem]) && !isUserRole &&
                <FabMenu menu={menu} />
            }
        </>
    );
};

export default RoleDetailsPage;